
import {defineComponent, ref, inject, watchEffect, onMounted, onUnmounted} from "vue";
import { useStore } from "vuex";
import { Apollo, Notify } from "@/core/services";
import { GET_TAX_RULE, SEARCH_TAX_RULE } from "../graphql/Queries";
import { DELETE_TAX_RULE } from "../graphql/Mutations";
import { useI18n } from "vue-i18n";
import Search from "../../../../components/search/Search.vue";
import Table from "../../../../components/Table/Table.vue";
import InnerLoader from "../../../../components/InnerLoader.vue";
import { dropdown_handler } from "../../../../core/helpers/dropdownHandler";

export default defineComponent({
	name: "tax-rule-list",
	components: { Search, Table, InnerLoader },
	setup() {
		const store = useStore();
		const i18n = useI18n();
		const emitter: any = inject("emitter");
		const taxRules = ref([]);
		const system_locale = ref();
		const pagination = ref({}) as Record<any, any>;
		const loader = ref(false);
		const loading = ref(false);
		const clickHandler = ref() as Record<any, any>;
		const currentPage = ref();
		const permissions = store.getters.getPermissions;
		const activeIndex = ref(-1);
    const searchObservable: any = ref(null);
    const observable: any = ref(null);
		const columns = ref([
			{
				label: "message.NAME",
				key: "name",
			},
			{
				label: "message.TAX",
				key: "tax",
			},
			{
				label: "message.CREATED_AT",
				key: "created_at",
			},
			{
				label: "message.ACTIONS",
				key: "actions",
				textAlignment: "center",
			},
		]);

		const filterObject = ref([
			{
				label: "message.NAME",
				value: "name",
			},
			{
				label: "message.TAX_NAME",
				value: "tax",
			},
		]);

		const parseLocaleName = (jsonName: string | null | undefined) => {
			if (!jsonName) return "";
			try {
				const parsed = typeof jsonName === "string" ? JSON.parse(jsonName) : jsonName;
				const locale = system_locale.value;
				const fallback = Object.values(parsed || {})[0] as { name?: string } | undefined;
				return parsed?.[locale]?.name || parsed?.en?.name || fallback?.name || "";
			} catch {
				return jsonName;
			}
		};

		const dropdownHandler = (index, id) => {
			activeIndex.value = index === id ? null : id;
		};
		const handleClickOutside = (event) => {
			if (!event.target.closest(".action-btn")) {
				activeIndex.value = -1;
			}
		};

		const getTaxRules = (page = 1, reload = false) => {
      if (searchObservable.value != null) {
        searchObservable.value?.unsubscribe();
        searchObservable.value = null;
      }
      if(page == 1) observable.value?.unsubscribe();
      loader.value = true;
      observable.value = Apollo.watchQuery({
				query: GET_TAX_RULE,
				fetchPolicy: "network-only",
				nextFetchPolicy: "cache-and-network",
				errorPolicy: "all",
				variables: {
					page: page,
					limit: 10,
				},
			}).subscribe(({ data, errors }) => {
				if (errors) {
					loader.value = false;
				}
				reload == true ? (currentPage.value = 1) : false;
				clickHandler.value = getTaxRules;
				taxRules.value = data?.tax_rules?.data || [];
				pagination.value = data?.tax_rules || {};
				loader.value = false;
			});
		};

		watchEffect(() => {
			system_locale.value = i18n.fallbackLocale.value;
		});

		getTaxRules();

    const searchHandler = (page = 1) => {
      if(observable.value != null) {
        observable.value?.unsubscribe();
        observable.value = null
      }
      searchObservable.value?.unsubscribe();
      const searchData = store.getters.getSearchData;
      loader.value = true;
      searchObservable.value = Apollo.watchQuery({
        query: SEARCH_TAX_RULE,
        errorPolicy: "all",
        fetchPolicy: "network-only",
        nextFetchPolicy: "network-only",
        variables: {
          page: page,
          limit: 10,
          filter: searchData.data.select,
          search_key: searchData.data.input,
          locale: system_locale.value,
        },
      }).subscribe(({ data, errors }) => {
        if (errors) {
          loader.value = false;
        }

        clickHandler.value = searchHandler;
        taxRules.value = data?.search_tax_rules?.data || [];
        pagination.value = data?.search_tax_rules || {};
        loader.value = false;
      });
    };

		const handleDelete = (id: number) => {
			store.getters.appInstance.$messageBox
				.confirm(`${i18n.t("message.ARE_YOU_SURE_TO_DELETE_RECORD")}?`, i18n.t("message.INFO"), {
					confirmButtonText: i18n.t("message.OK"),
					cancelButtonText: i18n.t("message.CANCEL"),
					type: "info",
				})
				.then(async () => {
					loader.value = true;
					await Apollo.mutate({
						mutation: DELETE_TAX_RULE,
						variables: { id: id },
						update: (store, { data: { delete_tax_rule } }) => {
              if (searchObservable.value != null) searchHandler(pagination.value.current_page)
              else getTaxRules(pagination.value.current_page)
							loader.value = false;
						},
					});
					Notify.success(i18n.t("message.RECORD_DELETED_SUCCESSFULLY"));
				})
				.catch(() => {
					loader.value = false;
				});
		};

		const handleCurrentChange = (num) => {
			clickHandler.value(num);
		};

		const exportFile = (data) => {
			const searchData = store.getters.getSearchData;
			loading.value = true;
			const sub = Apollo.watchQuery({
				query: SEARCH_TAX_RULE,
				errorPolicy: "all",
				fetchPolicy: "network-only",
				variables: {
					page: 0,
					limit: 1,
					filter: searchData?.data?.select ? searchData?.data?.select : "",
					search_key: searchData?.data?.input ? searchData?.data?.input : "",
					locale: system_locale.value,
					type: data?.type,
					date_from: data?.dateFrom,
					date_to: data?.dateTo,
				},
			}).subscribe(({ data, errors }) => {
				if (errors) {
					loading.value = false;
				} else if (data) {
					window.open(data?.search_tax_rules?.data[0]?.path, "_blank");
					loading.value = false;
				}

				loading.value = false;
				sub.unsubscribe();
			});
		};

		onMounted(() => {
			document.addEventListener("click", handleClickOutside);
		});

    onUnmounted(() => {
      observable.value?.unsubscribe();
      searchObservable.value?.unsubscribe();
    });

		const handleEdit = (tax_rule: Record<any, any>) => {
			emitter.emit("editTaxRuleAction", tax_rule);
		};

		return {
			taxRules,
			pagination,
			system_locale,
			filterObject,
			currentPage,
			columns,
			permissions,
			loading,
			loader,
			exportFile,
			getTaxRules,
			parseLocaleName,
			handleEdit,
			handleDelete,
			searchHandler,
			handleCurrentChange,
			activeIndex,
			dropdownHandler,
			handleClickOutside,
		};
	},
});
